import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { api } from "@/lib/api";
import { Activity, Moon, Crown, CheckCircle2, XCircle } from "lucide-react";
import { toast } from "sonner";

const fmtDay = (isoStr) => (isoStr ? isoStr.slice(0, 10) : "—");

// Phase 17.5: admin collectives health — takeover offers queue + active
// collectives with a dormant filter (last activity + leader recency).
export function CollectivesHealth() {
  const [takeovers, setTakeovers] = useState([]);
  const [collectives, setCollectives] = useState([]);
  const [dormantOnly, setDormantOnly] = useState(false);
  const [busy, setBusy] = useState(null);

  const load = () => {
    api.adminTakeovers().then(setTakeovers).catch(() => setTakeovers([]));
    api.adminCollectives("active").then(setCollectives).catch(() => setCollectives([]));
  };
  useEffect(() => { load(); }, []);

  const decide = async (t, action) => {
    setBusy(t.id);
    try {
      if (action === "approve") await api.approveTakeover(t.id);
      else await api.declineTakeover(t.id);
      setTakeovers((list) => list.filter((x) => x.id !== t.id));
      toast.success(action === "approve"
        ? `${t.requester_name} now leads ${t.collective_name}`
        : "Offer declined — the requester has been told");
      if (action === "approve") load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not update"); }
    setBusy(null);
  };

  const shown = dormantOnly ? collectives.filter((c) => c.dormant) : collectives;
  const dormantCount = collectives.filter((c) => c.dormant).length;

  return (
    <div className="mt-14" data-testid="admin-collectives-health">
      <h2 className="font-display text-2xl text-forest flex items-center gap-2">
        <Activity className="w-5 h-5 text-terracotta" /> Collective health
      </h2>
      <p className="text-neutral-600 text-sm mt-1">
        Dormant collectives and offers from members to take over leadership.
      </p>

      {takeovers.length > 0 && (
        <div className="mt-5 space-y-4">
          {takeovers.map((t) => (
            <Card key={t.id} className="border-amber-200 bg-amber-50/40" data-testid={`takeover-request-${t.id}`}>
              <CardContent className="p-6">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div>
                    <div className="font-display text-lg text-forest inline-flex items-center gap-2">
                      <Crown className="w-4 h-4 text-terracotta" /> {t.collective_name}
                    </div>
                    <div className="text-sm text-neutral-700 mt-1">
                      <span className="font-medium">{t.requester_name}</span> offers to lead
                    </div>
                    <div className="text-sm text-neutral-600 mt-2 italic bg-white border border-neutral-200 rounded-lg px-3 py-2 max-w-xl">
                      "{t.motivation}"
                    </div>
                    <div className="text-xs text-neutral-600 mt-2 flex flex-wrap gap-x-4 gap-y-1">
                      <span>Last activity: <span className="font-medium">{fmtDay(t.last_activity_at)}</span></span>
                      <span>{t.member_count} member{t.member_count !== 1 ? "s" : ""}</span>
                      {(t.leaders || []).map((l, i) => (
                        <span key={i}>Leader {l.name} last active: <span className="font-medium">{fmtDay(l.last_active)}</span></span>
                      ))}
                    </div>
                  </div>
                  <Badge className="bg-amber-100 text-amber-800 border-none">Offer pending</Badge>
                </div>
                <div className="flex gap-2 mt-4">
                  <Button data-testid={`takeover-approve-${t.id}`} disabled={busy === t.id} onClick={() => decide(t, "approve")} className="rounded-full bg-forest hover:bg-forest-hover h-9 px-4 text-sm">
                    <CheckCircle2 className="w-4 h-4 mr-1.5" /> Approve takeover
                  </Button>
                  <Button data-testid={`takeover-decline-${t.id}`} disabled={busy === t.id} onClick={() => decide(t, "decline")} variant="outline" className="rounded-full h-9 px-4 text-sm">
                    <XCircle className="w-4 h-4 mr-1.5" /> Decline
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <div className="mt-5 flex items-center gap-3">
        <button
          data-testid="admin-dormant-filter"
          onClick={() => setDormantOnly((v) => !v)}
          className={`text-sm rounded-full px-4 py-1.5 border transition-colors ${dormantOnly ? "bg-forest text-white border-forest" : "border-neutral-300 text-neutral-600 hover:border-forest/50"}`}
        >
          <Moon className="w-3.5 h-3.5 inline mr-1.5 -mt-0.5" />
          Dormant only ({dormantCount})
        </button>
        <span className="text-xs text-neutral-500">{shown.length} of {collectives.length} active collectives</span>
      </div>

      <div className="mt-3 space-y-2">
        {shown.length === 0 && <div className="text-sm text-neutral-500">Nothing here.</div>}
        {shown.map((c) => (
          <div key={c.id} data-testid={`health-row-${c.id}`} className="flex items-center justify-between gap-3 bg-white border border-neutral-200 rounded-xl px-4 py-3 flex-wrap">
            <div className="min-w-0">
              <div className="text-sm font-medium text-forest truncate">{c.name}</div>
              <div className="text-xs text-neutral-500">{c.area_label} · {c.member_count} member{c.member_count !== 1 ? "s" : ""}</div>
            </div>
            <div className="text-xs text-neutral-600 flex items-center gap-4">
              <span>Last activity: <span className="font-medium">{fmtDay(c.last_activity_at)}</span></span>
              <span>Leader active: <span className="font-medium">{fmtDay(c.leader_last_active)}</span></span>
              {c.dormant ? (
                <Badge data-testid={`dormant-badge-${c.id}`} variant="outline" className="rounded-full border-neutral-300 text-neutral-500 text-[10px]">
                  <Moon className="w-3 h-3 mr-1" /> Dormant
                </Badge>
              ) : (
                <Badge className="rounded-full bg-forest/10 text-forest border-none text-[10px]">Active</Badge>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
